import React, { useState, useEffect } from "react";
import "./EditProfile.css";
import apiClient from "../utils/apiClient";

const EditProfile = () => {
  const [formData, setFormData] = useState({
    employeeName: "",
    employeeId: "",
    email: "",
    primaryContact: "",
    secondaryContact: "",
    emergencyContact: "",
    joiningDate: "",
    dob: "",
    gender: "",
    bloodGroup: "",
    weekOff: "",
    address: "",
  });

  const [originalData, setOriginalData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ================= FETCH PROFILE ================= */
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await apiClient.get("/auth/me/");
        const data = response.data;

        const mappedData = {
          employeeName: data.name || "",
          employeeId: data.employee_id || "",
          email: data.email || "",
          primaryContact: data.phone || "",
          secondaryContact: data.secondary_phone || "",
          emergencyContact: data.emergency_contact || "",
          joiningDate: data.joining_date || "",
          dob: data.dob || "",
          gender: data.gender || "",
          bloodGroup: data.blood_group || "",
          weekOff: Array.isArray(data.week_off)
            ? data.week_off.join(", ")
            : data.week_off || "",
          address: data.address || "",
        };

        setFormData(mappedData);
        setOriginalData(mappedData);
      } catch (err) {
        console.error(err);
        setError("Error fetching profile data.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  /* ================= INPUT CHANGE ================= */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Allow only numbers for contact fields
    if (
      ["primaryContact", "secondaryContact", "emergencyContact"].includes(name) &&
      !/^\d*$/.test(value)
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ================= SAVE ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (
      (formData.primaryContact && formData.primaryContact.length !== 10) ||
      (formData.secondaryContact && formData.secondaryContact.length !== 10) ||
      (formData.emergencyContact && formData.emergencyContact.length !== 10)
    ) {
      setError("Contact numbers must be exactly 10 digits.");
      return;
    }

    try {
      await apiClient.put("/auth/me/", {
        phone: formData.primaryContact,
        secondary_phone: formData.secondaryContact,
        emergency_contact: formData.emergencyContact,
        blood_group: formData.bloodGroup,
        address: formData.address,
      });

      alert("✅ Profile updated successfully!");
      setIsEditing(false);
      setOriginalData(formData);
    } catch (err) {
      console.error(err);
      setError("Error updating profile.");
    }
  };

  /* ================= CANCEL ================= */
  const handleCancel = () => {
    setFormData(originalData);
    setIsEditing(false);
    setError("");
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="edit-profile-container">
      <h2>Edit Profile</h2>

      {error && <p className="error">{error}</p>}

      <form onSubmit={handleSubmit} className="edit-profile-form">
        {/* READ ONLY */}
        {[
          ["employeeName", "Employee Name"],
          ["employeeId", "Employee ID"],
          ["email", "Email"],
          ["joiningDate", "Joining Date"],
          ["dob", "Date of Birth"],
          ["gender", "Gender"],
          ["weekOff", "Week Off"],
        ].map(([name, label]) => (
          <div className="form-group" key={name}>
            <label>{label}</label>
            <input type="text" value={formData[name]} disabled />
          </div>
        ))}

        {/* EDITABLE */}
        {[
          ["primaryContact", "Primary Contact"],
          ["secondaryContact", "Secondary Contact"],
          ["emergencyContact", "Emergency Contact"],
          ["bloodGroup", "Blood Group"],
        ].map(([name, label]) => (
          <div className="form-group" key={name}>
            <label>{label}</label>
            <input
              type="text"
              name={name}
              value={formData[name]}
              onChange={handleInputChange}
              disabled={!isEditing}
              maxLength={name.includes("Contact") ? 10 : undefined}
            />
          </div>
        ))}

        <div className="form-group">
          <label>Address</label>
          <textarea
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </div>

        <div className="form-actions">
          {isEditing ? (
            <>
              <button type="submit" className="submit-btn">
                Save Changes
              </button>
              <button type="button" className="cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="edit-btn"
              onClick={() => setIsEditing(true)}
            >
              Edit Profile
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default EditProfile;
